import { motion } from 'framer-motion';

interface Props {
  src: string;
  alt?: string;
  square?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  borderColor?: string;
  particleColor1?: string;
  particleColor2?: string;
}

export default function PhotoWithBackdrop({ 
  src, 
  alt = "Photo", 
  square = false, 
  size = 'md',
  borderColor = "border-yellow-300",
  particleColor1 = "bg-yellow-300",
  particleColor2 = "bg-orange-300"
}: Props) {
  const sizeClasses = {
    sm: 'w-32 h-32',
    md: 'w-full h-64',
    lg: 'w-full h-80',
    xl: 'w-full h-96',
    full: 'w-full h-full'
  };

  const containerClass = square 
    ? `relative ${sizeClasses[size].split(' ')[0]} aspect-square`
    : `relative ${sizeClasses[size]}`;

  return (
    <motion.div 
      className={containerClass}
      whileHover={{ 
        scale: 1.02,
        y: -5,
        transition: { duration: 0.3 }
      }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* Animated backdrop */}
      <motion.div 
        className={`absolute -top-2 -left-2 w-full h-full border-2 border-dashed rounded-lg z-0 ${borderColor}`}
        animate={{ 
          rotate: [0, 1, -1, 0],
          scale: [1, 1.02, 1]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Image with hover zoom effect */}
      <motion.div className="relative z-10 w-full h-full overflow-hidden rounded-lg shadow-md">
        <motion.img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          whileHover={{ 
            scale: 1.1,
            transition: { duration: 0.4 }
          }}
        />
        
        {/* Subtle overlay on hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0"
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
      
      {/* Floating particles effect */}
      <motion.div
        className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${particleColor1}`}
        animate={{ 
          y: [0, -10, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className={`absolute -bottom-1 -left-1 w-2 h-2 rounded-full ${particleColor2}`}
        animate={{ 
          y: [0, -8, 0],
          opacity: [0.3, 0.8, 0.3]
        }}
        transition={{ 
          duration: 2.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      />
    </motion.div>
  );
}